/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { PrinterFinderListItem } from '@gh/builder-shared';
import {
  CommunicationAttachmentDto,
  JobOrderListDto,
  JobOrderListEstimatingCriteriaDto,
  JobOrderListItemDto,
  JobOrderListMasterInfoDto,
  JobOrderListProduceDto,
  JobOrderMethod,
  LetterDto,
} from '@gh/builder-shared-data';
import { formStateSnapshot, FormStateSnapshot } from '@gh/common-shared';
import { EMPTY_ID, ID, ProduceStatusDto, WithEntityState } from '@gh/common-shared-data';
import { emptySelectionModel, SelectionModel } from '@gh/core-data';
import { isNotNil } from '@gh/core-util';
import { inRange, pick, stubTrue } from 'lodash';

export type JobOrderListMasterInfo = {
  id: ID;
  jobNumber: string;
  contactName: string;
  contactId: ID;
  siteAddress: string;
  produceInProgress: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  isProceeding: boolean; // negate of isNotProceeding (necessary to map into form)
  jobDocsFolderId: ID;
  extraOrderDocs: CommunicationAttachmentDto[];
  isEstimateAuthorized: boolean;
  isCouncilApproved: boolean;
  isFinanceApproved: boolean;
  isColorSelectionSigned: boolean;
  isJobScheduled: boolean;
  isContractSigned: boolean;
  hasInsurancePolicyNumber: boolean;
  isJobCriteriaFailed: boolean; // if one of above criteria is failed
};
export type JobOrderListEstimatingCriteria = JobOrderListEstimatingCriteriaDto;
export type JobOrderListItem = WithEntityState<JobOrderListItemDto> & {
  isCanBeSelected: boolean; // affected by isCriteriaFailed, isJobCriteriaFailed and ordersPrintCostCenter
};
export type JobOrderListProduceOptions = { emailed: boolean; printed: boolean; forcePrint: boolean };
export type JobOrderDetailsAttachment = CommunicationAttachmentDto;
export type JobOrderList = {
  job: FormStateSnapshot<JobOrderListMasterInfo>;
  estimatingCriteria: JobOrderListEstimatingCriteria;
  orders: JobOrderListItem[];
  selectionType: JobOrderListSelectionType;
  orderSelection: SelectionModel;
  has6ADocument: SelectionModel;
  options: JobOrderListProduceOptions;
  attachments: JobOrderDetailsAttachment[];
  produceStatus: JobOrderListProduceStatus;
  reportUrl: string;
};
export type JobOrderLetter = LetterDto;
export type JobOrderListProduce = JobOrderListProduceDto;
export type JobOrderListProduceStatus = { jobId: ID; producing: boolean; progress?: ProduceStatusDto };
export type JobOrderListCcItem = { code: number; name: string };

export enum JobOrderListSelectionType {
  All = 'All',
  Selected = 'Selected',
  CostCenter = 'CostCenter',
  Order = 'Order',
}

export type JobOrderListSelectionCostCenterFilter = {
  type: JobOrderListSelectionType.CostCenter;
  from: number;
  to: number;
};
export type JobOrderListSelectionOrderFilter = {
  type: JobOrderListSelectionType.Order;
  orderNumber: number;
};
export type JobOrderListSelectionFilter = JobOrderListSelectionCostCenterFilter | JobOrderListSelectionOrderFilter;

export const JOB_ORDER_LIST_DEFAULT_PRODUCE_OPTIONS: JobOrderListProduceOptions = {
  forcePrint: false,
  emailed: false,
  printed: false,
};
export const JOB_ORDER_LIST_EMPTY: JobOrderList = {
  job: formStateSnapshot(<JobOrderListMasterInfo>{
    produceInProgress: false,
  }),
  estimatingCriteria: {
    criteriaEstimate: false,
    criteriaCouncil: false,
    criteriaFinance: false,
    criteriaColorSelection: false,
    criteriaJobSchedule: false,
    criteriaContract: false,
    criteriaJobProceeding: false,
    criteriaInsurancePolicy: false,
    printRecordForm: false,
  },
  orders: [],
  selectionType: JobOrderListSelectionType.Selected,
  orderSelection: emptySelectionModel(),
  has6ADocument: emptySelectionModel(),
  options: JOB_ORDER_LIST_DEFAULT_PRODUCE_OPTIONS,
  attachments: [],
  produceStatus: { jobId: EMPTY_ID, producing: false },
  reportUrl: '',
};

export const getJobOrderListItemId = (order: JobOrderListItem) => order.jobOrderId;
export const isJobOrderProduced = (order: JobOrderListItem) => isNotNil(order.producedDate);

const jobOrderListMasterInfoFromDto = (dto: JobOrderListMasterInfoDto,
                                       estimatingCriteria: JobOrderListEstimatingCriteria): JobOrderListMasterInfo => {
  const isEstimateAuthorized = isNotNil(dto.estimateAuthorizedDate);
  const { isCouncilApproved, isFinanceApproved, isContractSigned } = dto;
  const isColorSelectionSigned = isNotNil(dto.csSignedByClientDate) && isNotNil(dto.csSignedByOfficeDate);
  const isJobScheduled = isNotNil(dto.scheduleStartDate);
  const isProceeding = !dto.isNotProceeding;
  const hasInsurancePolicyNumber = isNotNil(dto.insurancePolicyNumber);

  return {
    ...pick(dto, [
      'id', 'jobNumber', 'contactName', 'siteAddress', 'produceInProgress', 'isFinalized', 'isNotProceeding',
      'jobDocsFolderId', 'extraOrderDocs', 'contactId',
    ]),
    isEstimateAuthorized, isCouncilApproved, isFinanceApproved, isContractSigned, isColorSelectionSigned,
    isJobScheduled, isProceeding, hasInsurancePolicyNumber,
    isJobCriteriaFailed: !(
      // check each estimating criteria: if we have to check it => verify corresponding condition
      (!estimatingCriteria.criteriaEstimate || isEstimateAuthorized)
      && (!estimatingCriteria.criteriaCouncil || isCouncilApproved)
      && (!estimatingCriteria.criteriaFinance || isFinanceApproved)
      && (!estimatingCriteria.criteriaContract || isContractSigned)
      && (!estimatingCriteria.criteriaColorSelection || isColorSelectionSigned)
      && (!estimatingCriteria.criteriaJobSchedule || isJobScheduled)
      && (!estimatingCriteria.criteriaJobProceeding || isProceeding)
      && (!estimatingCriteria.criteriaInsurancePolicy || hasInsurancePolicyNumber)),
  };
};

const jobOrderFromDto = (order: JobOrderListItemDto, isJobCriteriaFailed: boolean, ordersPrintCostCenter?: number) => ({
  ...order,
  // if job criteria is failed => order can be selected only if ordersPrintCostCenter is set
  // and it is more or equals to order CC
  isCanBeSelected: !order.isCriteriaFailed && (
    !isJobCriteriaFailed || ordersPrintCostCenter && order.costCenterCode <= ordersPrintCostCenter || false),
});

export const jobOrderListFromDto = ({ job, jobOrders, estimatingCriteria }: JobOrderListDto,
                                    produceStatus: JobOrderListProduceStatus,
                                    reportUrl: string): JobOrderList => {
  const masterInfo = jobOrderListMasterInfoFromDto(job, estimatingCriteria);
  return {
    job: formStateSnapshot(masterInfo),
    orders: jobOrders.map((order) => jobOrderFromDto(
      order,
      masterInfo.isJobCriteriaFailed,
      estimatingCriteria.ordersPrintCostCenter)),
    estimatingCriteria,
    selectionType: JobOrderListSelectionType.Selected,
    orderSelection: emptySelectionModel(),
    has6ADocument: emptySelectionModel(),
    options: JOB_ORDER_LIST_DEFAULT_PRODUCE_OPTIONS,
    attachments: job.extraOrderDocs,
    produceStatus,
    reportUrl,
  };
};

export const jobOrderListProduce = (list: JobOrderList,
                                    isCloud: boolean,
                                    printer?: PrinterFinderListItem,
                                    authCode?: string): JobOrderListProduce => {
  const selectedOrderIds = list.orders
    // get all order satisfying SelectionModel
    .filter((order) => list.orderSelection.idList.includes(getJobOrderListItemId(order)))
    // filter orders depending on printer existence
    .filter((!isCloud || printer) ? stubTrue : (order) => order.method === JobOrderMethod.Email)
    // get order IDs
    .map(getJobOrderListItemId);

  return {
    printerId: printer && printer.cloudPrinterId,
    forcePrint: list.options.forcePrint,
    attachToEmailed: list.options.emailed,
    attachToPrinted: list.options.printed,
    jobOrderIds: selectedOrderIds,
    jobOrderIds6a: selectedOrderIds.filter((orderId) => list.has6ADocument.idList.includes(orderId)),
    attachments: list.attachments,
    authCode,
  };
};

export const jobOrderListPrintAll = (): JobOrderListProduce => ({
  // printerId: printer && printer.cloudPrinterId,
  forcePrint: false,
  attachToEmailed: false,
  attachToPrinted: false,
  jobOrderIds: [],
  jobOrderIds6a: [],
  attachments: [],
  // authCode,
});

export const jobOrderListProduceStatusFromDto = (jobId: ID,
                                                 progress: ProduceStatusDto): JobOrderListProduceStatus => ({
  jobId,
  producing: progress.processed !== progress.total,
  progress,
});

export const jobOrderListNoProducing = (jobId: ID): JobOrderListProduceStatus => ({ jobId, producing: false });

export const isJobOrderSelectable = (order: JobOrderListItem) => order.isCanBeSelected;

export const createJobOrderListPredicate = (filter: JobOrderListSelectionFilter): PredicateFn<JobOrderListItem> =>
  filter.type === JobOrderListSelectionType.CostCenter
    ? (order) => isJobOrderSelectable(order) && inRange(order.costCenterCode, filter.from, filter.to + 1)
    : (order) => isJobOrderSelectable(order) && order.orderNumber === filter.orderNumber;
